import { AsYouType, isValidPhoneNumber } from "libphonenumber-js"
import { createStyles, makeStyles, Typography } from "@material-ui/core"
import clsx from "clsx"
import QueryString from "query-string"
import { useEffect, useState } from "react"
import { useLocation } from "react-router-dom"
import { useNavigation } from "../../hooks/useNavigation"
import { useSession } from "../../hooks/useSession"
import { colors } from "../../utils/theme"
import { GhostButton, PrimaryButton } from "../atoms/button/Button"
import Input from "../atoms/input/Input"
import Logo from "../atoms/Logo"
import PasswordRules from "../molecules/PasswordRules"
import { TitledCard } from "../molecules/titledCard/TitledCard"
import { BaseTemplate } from "../templates/BaseTemplate"
import { DefaultCountryCode, Role, DefaultCountry } from "../../utils/constants"
import MobileVerifyDialog from "../organisms/MobileVerifyDialog"
import MobileUpdateDialog from "../organisms/MobileUpdateDialog"

const useStyles = makeStyles((theme) =>
  createStyles({
    register: {
      display: "flex",
      flex: 1,
      flexDirection: "column",
      height: "100vh",
      backgroundColor: colors.primary.cloud,
      alignItems: "center",
      justifyContent: "center",
    },
    button: {
      width: "100%",
    },
    marginBottom16: {
      marginBottom: theme.spacing(2),
    },
    marginBottom8: {
      marginBottom: theme.spacing(1),
    },
    card: {
      width: 440,
      "& [class^=makeStyles-card-]": {
        width: "440px !important",
      },
    },
    registerContainer: {
      display: "flex",
      alignItems: "center",
      alignSelf: "center",
    },
    logo: {
      position: "absolute",
      top: theme.spacing(6),
      left: theme.spacing(6),
    },
    center: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    input: {
      width: "100%",
      marginBottom: theme.spacing(2),
    },
    login: {
      color: colors.neutral.asphalt,
      marginRight: 4,
    },
    subtitle: {
      fontWeight: 400,
      color: colors.neutral.denim,
      marginBottom: theme.spacing(4),
    },
    inputContainer: {
      position: "relative",
      width: "100%",
    },
    marginBottom32: {
      marginBottom: theme.spacing(4),
    },
    denim: {
      color: colors.neutral.denim,
    },
  })
)

const DEAULT_MOBILE_HELPTEXT = "Used for risk alert notifications"

const Register = () => {
  const classes = useStyles()
  const { register } = useSession()
  const [email, setEmail] = useState<string>("")
  const [userId, setUserId] = useState<string>("")
  const [role, setRole] = useState<string>("")
  const [firstName, setFirstName] = useState<string>("")
  const [lastName, setLastName] = useState<string>("")
  const [password, setPassword] = useState<string>("")
  const [mobile, setMobile] = useState<string>("")
  const [isInvalidMoible, setIsInvalidMobile] = useState<boolean>(false)
  const [mobileError, setMobileError] = useState<string>(DEAULT_MOBILE_HELPTEXT)
  const [emailError, setEmailError] = useState<string>("")
  const [reenteredPassword, setReenteredPassword] = useState<string>("")
  const [passwordError, setPasswordError] = useState<string | undefined>(
    undefined
  )
  const [reenteredPasswordError, setReenteredPasswordError] = useState<
    string | undefined
  >(undefined)
  const [firstNameError, setFirstNameError] = useState<string | undefined>(
    undefined
  )
  const [lastNameError, setLastNameError] = useState<string | undefined>(
    undefined
  )
  const [passwordFocus, setPasswordFocus] = useState<boolean>(false)
  const [reenteredPasswordFocus, setReenteredPasswordFocus] =
    useState<boolean>(false)
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [openMobileVerifyDialog, setOpenMobileVerifyDialog] =
    useState<boolean>(false)
  const [openMobileUpdateDialog, setOpenMobileUpdateDialog] =
    useState<boolean>(false)
  const [validUrl, setValidUrl] = useState<boolean>(false)
  const location = useLocation()
  const queryString = QueryString.parse(location.search) as {
    email: string
    firstName: string
    lastName: string
  }
  const { toLogin, toDashboard, toMyOrg } = useNavigation()

  useEffect(() => {
    if (validUrl) {
      return
    }
    if (
      queryString.email &&
      queryString.email.length > 0 &&
      queryString.firstName &&
      queryString.firstName.length > 0 &&
      queryString.lastName &&
      queryString.lastName.length > 0
    ) {
      setValidUrl(true)
      setEmail(queryString.email)
      setFirstName(queryString.firstName)
      setLastName(queryString.lastName)
    }
  }, [queryString, validUrl])

  const closeDialog = (isMobileUpdateDialog: boolean) => {
    if (isMobileUpdateDialog) {
      setOpenMobileUpdateDialog(false)
    } else {
      setOpenMobileVerifyDialog(false)
    }
    if (role === Role.ADMIN) {
      toMyOrg()
    } else {
      toDashboard()
    }
  }

  const submit = async () => {
    setIsInvalidMobile(false)
    setMobileError(DEAULT_MOBILE_HELPTEXT)
    setPasswordError(undefined)
    setReenteredPasswordError(undefined)
    setFirstNameError(undefined)
    setLastNameError(undefined)
    const errors = {
      firstName: false,
      lastName: false,
      password: false,
      reenteredPassword: false,
      mobile: false,
    }
    if (
      mobile &&
      !isValidPhoneNumber(DefaultCountryCode + " " + mobile, DefaultCountry)
    ) {
      setIsInvalidMobile(true)
      setMobileError("Please enter a valid mobile number.")
      errors.mobile = true
    }
    if (firstName === "") {
      errors.firstName = true
      setFirstNameError("You need to enter a first name to continue.")
    }
    if (lastName === "") {
      errors.lastName = true
      setLastNameError("You need to enter a last name to continue.")
    }
    if (password === "") {
      errors.password = true
      setPasswordError("You need to enter a valid password to continue.")
    }
    if (reenteredPassword === "") {
      errors.reenteredPassword = true
      setReenteredPasswordError(
        "You need to re-enter your password to continue."
      )
    } else if (password !== reenteredPassword || !validPassword) {
      errors.password = true
      errors.reenteredPassword = true
      setReenteredPasswordError("The passwords you enter must match.")
    }
    if (
      errors.firstName ||
      errors.lastName ||
      errors.password ||
      errors.reenteredPassword ||
      errors.mobile
    ) {
      return
    }
    try {
      const result = await register(
        email,
        password,
        firstName,
        lastName,
        mobile
      )
      if (typeof result !== "string" && result?.message) {
        setEmailError(result.message)
        return
      }
      const userId = result?.toString().split("@")[0]
      const role = result?.toString().split("@")[1]
      setUserId(userId ?? "")
      setRole(role ?? "")
      if (mobile) {
        setOpenMobileVerifyDialog(true)
      }
    } catch (error) {
      setEmailError((error as Error).message)
    }
  }

  return (
    <>
      <BaseTemplate>
        <div
          onKeyDown={async (event) => {
            if (event.key === "Enter") {
              await submit()
            }
          }}
        >
          <Logo className={classes.logo} />
          {validUrl ? (
            <div className={classes.card}>
              <TitledCard title="Welcome to Vira Pro!">
                <Typography variant="body1" className={classes.subtitle}>
                  To begin using Vira Pro, please provide the information below.
                </Typography>
                <Input
                  type="email"
                  autoComplete="email"
                  label="Email"
                  placeholder="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={clsx(classes.input)}
                  contentEditable={false}
                  disabled={true}
                  error={Boolean(emailError)}
                  helperText={emailError}
                />
                <Input
                  label="First Name"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className={clsx(classes.input)}
                  error={Boolean(firstNameError)}
                  helperText={firstNameError}
                  required={true}
                />
                <Input
                  label="Last Name"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className={clsx(classes.input)}
                  error={Boolean(lastNameError)}
                  helperText={lastNameError}
                  required={true}
                />
                <Input
                  label="Mobile Number"
                  placeholder="Mobile Number"
                  value={mobile}
                  onChange={(e) => {
                    const formatted = new AsYouType({
                      defaultCallingCode: DefaultCountryCode,
                    }).input(e.target.value)
                    setMobile(formatted)
                  }}
                  className={clsx(classes.input)}
                  error={isInvalidMoible}
                  helperText={mobileError}
                />
                <div className={classes.inputContainer}>
                  <Input
                    onFocus={() => setPasswordFocus(true)}
                    onBlur={() => setPasswordFocus(false)}
                    autoComplete="new-password"
                    type="password"
                    label="Password"
                    placeholder="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={clsx(classes.input)}
                    error={Boolean(passwordError)}
                    helperText={passwordError}
                    enableShowPassword={true}
                    required={true}
                  />
                  {passwordFocus && (
                    <PasswordRules
                      password={password}
                      comparisonPassword={reenteredPassword}
                      setValidPassword={setValidPassword}
                      left={326}
                    />
                  )}
                </div>
                <div className={classes.inputContainer}>
                  <Input
                    onFocus={() => setReenteredPasswordFocus(true)}
                    onBlur={() => setReenteredPasswordFocus(false)}
                    type="password"
                    label="Re-enter Password"
                    placeholder="Re-enter Password"
                    value={reenteredPassword}
                    onChange={(e) => setReenteredPassword(e.target.value)}
                    className={clsx(classes.input)}
                    error={Boolean(reenteredPasswordError)}
                    helperText={reenteredPasswordError}
                    enableShowPassword={true}
                    required={true}
                  />
                  {reenteredPasswordFocus && (
                    <PasswordRules
                      password={password}
                      comparisonPassword={reenteredPassword}
                      setValidPassword={setValidPassword}
                      left={326}
                    />
                  )}
                </div>
                <div className={classes.marginBottom16}>
                  <PrimaryButton onClick={submit} className={classes.button}>
                    Create Account
                  </PrimaryButton>
                </div>
                <div className={classes.registerContainer}>
                  <Typography variant="h6" className={classes.login}>
                    Already have an account?
                  </Typography>
                  <GhostButton onClick={() => toLogin()}>Log In</GhostButton>
                </div>
              </TitledCard>
            </div>
          ) : (
            <div className={classes.card}>
              <TitledCard title="Welcome to Vira Pro!">
                <Typography
                  variant="body1"
                  className={clsx(classes.marginBottom32, classes.denim)}
                >
                  Sorry, the link you followed is not valid.
                </Typography>
                <PrimaryButton className={classes.button} onClick={toLogin}>
                  Go to Login
                </PrimaryButton>
              </TitledCard>
            </div>
          )}
        </div>
      </BaseTemplate>
      <MobileVerifyDialog
        open={openMobileVerifyDialog}
        mobile={mobile}
        userId={userId}
        openMobileUpdateDialog={() => {
          setOpenMobileVerifyDialog(false)
          setOpenMobileUpdateDialog(true)
        }}
        verifySuccess={() => {
          closeDialog(false)
        }}
        closeCallback={() => {
          closeDialog(false)
        }}
        cancelButtonLabel="Continue without a mobile number"
      />
      <MobileUpdateDialog
        open={openMobileUpdateDialog}
        mobile={mobile}
        openMobileVerifyDialog={(updateMobile: string) => {
          setOpenMobileUpdateDialog(false)
          if (updateMobile) {
            setMobile(updateMobile)
            setOpenMobileVerifyDialog(true)
          }
        }}
        closeCallback={() => {
          closeDialog(true)
        }}
      />
    </>
  )
}

export default Register
